/**
 * Order Timeline Component
 *
 * Visual stepper showing order progression through different stages.
 * Completed steps: green checkmark
 * Current step: blue dot (pulsing animation)
 * Future steps: gray dot
 * Lines connect the dots
 * Below each step: timestamp if completed
 */

import type { Order, OrderStatus } from '../../types/widget/order';

interface OrderTimelineProps {
  order: Order;
  className?: string;
}

interface TimelineStep {
  id: string;
  label: string;
  statusMatch: OrderStatus[];
  getTimestamp: (order: Order) => string | null;
}

const timelineSteps: TimelineStep[] = [
  {
    id: 'confirmed',
    label: 'Order Confirmed',
    statusMatch: ['pending', 'scheduled', 'delivered', 'in_use', 'pickup_requested', 'picked_up', 'completed'],
    getTimestamp: (order) => order.createdAt,
  },
  {
    id: 'scheduled',
    label: 'Scheduled',
    statusMatch: ['scheduled', 'delivered', 'in_use', 'pickup_requested', 'picked_up', 'completed'],
    getTimestamp: (order) => order.scheduledDeliveryDate || null,
  },
  {
    id: 'delivered',
    label: 'Delivered',
    statusMatch: ['delivered', 'in_use', 'pickup_requested', 'picked_up', 'completed'],
    getTimestamp: (order) => order.actualDeliveryDate || null,
  },
  {
    id: 'in_use',
    label: 'In Use',
    statusMatch: ['in_use', 'pickup_requested', 'picked_up', 'completed'],
    getTimestamp: (order) => order.actualDeliveryDate || null,
  },
  {
    id: 'picked_up',
    label: 'Picked Up',
    statusMatch: ['picked_up', 'completed'],
    getTimestamp: (order) => order.actualPickupDate || null,
  },
  {
    id: 'completed',
    label: 'Completed',
    statusMatch: ['completed'],
    getTimestamp: (order) => order.updatedAt,
  },
];

function formatTimestamp(isoString: string | null): string {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function getStepStatus(
  _step: TimelineStep,
  order: Order,
  currentStepIndex: number,
  stepIndex: number
): 'completed' | 'current' | 'future' | 'cancelled' {
  if (order.status === 'cancelled') {
    return stepIndex < currentStepIndex ? 'completed' : 'cancelled';
  }

  if (stepIndex < currentStepIndex) {
    return 'completed';
  } else if (stepIndex === currentStepIndex) {
    return 'current';
  } else {
    return 'future';
  }
}

export function OrderTimeline({ order, className = '' }: OrderTimelineProps) {
  // Find current step index
  const currentStepIndex = timelineSteps.findIndex((step) =>
    step.statusMatch.includes(order.status)
  );

  // Handle cancelled orders specially
  if (order.status === 'cancelled') {
    return (
      <div className={`py-8 ${className}`}>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
            <span className="text-2xl">✕</span>
          </div>
          <p className="text-lg font-medium text-gray-900">Order Cancelled</p>
          <p className="text-sm text-gray-500 mt-1">
            {formatTimestamp(order.updatedAt)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`py-6 ${className}`}>
      <div className="flex items-start justify-between relative">
        {timelineSteps.map((timelineStep, index) => {
          const status = getStepStatus(timelineStep, order, currentStepIndex, index);
          const timestamp = timelineStep.getTimestamp(order);
          const isLastStep = index === timelineSteps.length - 1;

          return (
            <div key={timelineStep.id} className="flex-1 relative">
              <div className="flex flex-col items-center">
                {/* Step indicator */}
                <div
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2 z-10
                    ${
                      status === 'completed'
                        ? 'bg-green-500 border-green-500 text-white'
                        : status === 'current'
                        ? 'bg-blue-500 border-blue-500 text-white animate-pulse'
                        : 'bg-white border-gray-300 text-gray-400'
                    }
                  `}
                >
                  {status === 'completed' ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-current" />
                  )}
                </div>

                {/* Step label */}
                <div className="text-center mt-3">
                  <p
                    className={`text-sm font-medium ${
                      status === 'completed' || status === 'current'
                        ? 'text-gray-900'
                        : 'text-gray-400'
                    }`}
                  >
                    {timelineStep.label}
                  </p>
                  {timestamp && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTimestamp(timestamp)}
                    </p>
                  )}
                </div>
              </div>

              {/* Connecting line */}
              {!isLastStep && (
                <div
                  className={`
                    absolute top-5 left-1/2 w-full h-0.5 -z-10
                    ${
                      index < currentStepIndex
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                    }
                  `}
                  style={{ transform: 'translateX(-50%)' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
